"use client";

import { useEffect } from "react";
import { viatorCitySearchUrl } from "@/app/lib/affiliate";

// Enhances the LLM-generated itinerary HTML inside #itineraryOutput without
// touching the prompt contract:
//   1. Fires GA (gtag) events for affiliate/Directions link clicks + a GYG
//      widget impression, so booking engagement is finally measurable.
//   2. Decorates Viator anchors as clear "Book" actions and appends one
//      per-day "Book tours & tickets in {city}" CTA.
//
// All injected nodes/classes carry the `affiliate-book-*` marker so
// ItineraryDayActions.serializeItinerary() can strip them before persisting
// (never bake CTAs into saved HTML). A MutationObserver re-applies the
// enhancement after a day is regenerated or an activity is removed — this
// component stays intentionally decoupled from ItineraryDayActions, which
// separately assigns the `day-N` section ids we read for `day_id`.
export default function AffiliateTracking({
  city,
  cityName,
}: {
  city: string;
  cityName: string;
}) {
  useEffect(() => {
    const output = document.getElementById("itineraryOutput");
    if (!output) return;

    const label = cityName || city;
    const ctaHref = viatorCitySearchUrl(label);

    const track = (name: string, params: Record<string, unknown>) => {
      try {
        (
          window as unknown as {
            gtag?: (...args: unknown[]) => void;
          }
        ).gtag?.("event", name, params);
      } catch {
        /* analytics is best-effort */
      }
    };

    // First-party click log (own-able, ad-blocker-resilient). Only sent for the
    // actual click events, not impressions. sendBeacon survives navigation;
    // fetch(keepalive) is the fallback.
    const report = (payload: Record<string, unknown>) => {
      try {
        const body = JSON.stringify(payload);
        const nav = navigator as Navigator & {
          sendBeacon?: (url: string, data?: BodyInit) => boolean;
        };
        if (typeof nav.sendBeacon === "function") {
          const blob = new Blob([body], { type: "application/json" });
          if (nav.sendBeacon("/api/track-affiliate", blob)) return;
        }
        fetch("/api/track-affiliate", {
          method: "POST",
          body,
          keepalive: true,
          headers: { "Content-Type": "application/json" },
        }).catch(() => {});
      } catch {
        /* best-effort */
      }
    };

    const dayIdOf = (el: Element | null) =>
      el?.closest("section[id^='day-']")?.id ?? "";

    // --- 1. Delegated click tracking -------------------------------------
    const onClick = (event: Event) => {
      const anchor = (event.target as HTMLElement).closest("a");
      if (!anchor) return;
      const href = (anchor.getAttribute("href") || "").toLowerCase();
      if (!href) return;
      const day_id = dayIdOf(anchor);
      if (href.includes("viator.com")) {
        track("affiliate_click", { provider: "viator", city, day_id });
        report({
          eventType: "affiliate_click",
          provider: "viator",
          city,
          dayId: day_id,
        });
      } else if (
        href.includes("google.com/maps") ||
        href.includes("maps.google")
      ) {
        track("directions_click", { city, day_id });
        report({ eventType: "directions_click", city, dayId: day_id });
      }
    };
    output.addEventListener("click", onClick);

    // --- 2. DOM enhancement (idempotent) ---------------------------------
    const enhance = () => {
      output
        .querySelectorAll<HTMLAnchorElement>("a[href*='viator.com']")
        .forEach((a) => {
          if (a.classList.contains("affiliate-book-link")) return;
          a.classList.add("affiliate-book-link");
          a.setAttribute("target", "_blank");
          a.setAttribute("rel", "sponsored noopener");
        });

      output.querySelectorAll("section").forEach((section) => {
        if (section.querySelector(":scope > .affiliate-book-cta")) return;
        const cta = document.createElement("a");
        cta.className = "affiliate-book-cta print:hidden";
        cta.href = ctaHref;
        cta.target = "_blank";
        cta.rel = "sponsored noopener";
        cta.textContent = `Book tours & tickets in ${label} →`;
        section.appendChild(cta);
      });
    };

    // Re-apply after day regeneration / activity removal mutates the DOM.
    // Disconnect while we mutate so our own additions don't re-trigger us.
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => {
        scheduled = false;
        observer.disconnect();
        enhance();
        observer.observe(output, { childList: true, subtree: true });
      });
    });

    enhance();

    const gygCount = output.querySelectorAll("[data-gyg-widget]").length;
    if (gygCount > 0) {
      track("gyg_widget_impression", { city, count: gygCount });
    }

    observer.observe(output, { childList: true, subtree: true });

    return () => {
      output.removeEventListener("click", onClick);
      observer.disconnect();
      output
        .querySelectorAll(".affiliate-book-cta")
        .forEach((el) => el.remove());
      output
        .querySelectorAll(".affiliate-book-link")
        .forEach((el) => el.classList.remove("affiliate-book-link"));
    };
  }, [city, cityName]);

  return null;
}
